import { Injectable } from '@angular/core';

import { Partners, Quote } from '@lockton-apps/shared/interfaces';
import { createDefinition } from '@lockton-apps/shared/util/helpers';
import { WorkflowMetaPipe } from '@lockton-apps/shared/ui/pipes';
import { clamp, compact, invoke } from 'lodash';
import { QuotesApiActionTypes } from '@lockton-apps/shared/action/quotes';
import { ProposalStepActionTypes } from '@lockton-apps/proposals/action';
import { QuotesEffectsActionTypes } from '@lockton-apps/shared/data-access/quotes';
import { addMilliseconds, differenceInMilliseconds } from 'date-fns';
import { DEFAULT_SUBTLE_MESSAGE_OPTIONS } from '@lockton-apps/shared/ui/lui';

export interface FeedbackMessage {
  text: string;
  type: 'info' | 'success' | 'error';
  delay: number;
}

@Injectable({
  providedIn: 'root',
})
export class FeedbackMessagesService {
  private nextSlot: Date = new Date();

  constructor(private readonly workflowMeta: WorkflowMetaPipe) {}

  getMessageFor(
    type: string,
    portalId: Partners,
    data?: any
  ): FeedbackMessage | null {
    const message: FeedbackMessage | null = invoke(
      this.map(compact([type, portalId])),
      'create',
      data
    );

    if (!message) {
      return null;
    }

    const { delay, nextSlot } = this.timings;

    this.nextSlot = nextSlot;

    return {
      ...message,
      delay,
    };
  }

  private readonly map = createDefinition<{
    create: (quote: Quote) => Partial<FeedbackMessage>;
  }>({
    base: {},
    variants: {
      // Proposals
      [ProposalStepActionTypes.ConnectionRestored]: {
        base: {
          create: () => ({
            text: $localize`Internet connection restored`,
            type: 'info',
          }),
        },
      },
      [ProposalStepActionTypes.ConnectionLost]: {
        base: {
          create: () => ({
            text: $localize`Internet connection lost`,
            type: 'info',
          }),
        },
      },

      // Quotes
      [QuotesEffectsActionTypes.ProposalBackupSaved]: {
        base: {
          create: () => ({
            text: $localize`Proposal form successfully saved`,
            type: 'info',
          }),
        },
      },
      [QuotesApiActionTypes.ProposalUpdatedSuccess]: {
        base: {
          create: () => ({
            text: $localize`Proposal form successfully saved`,
            type: 'info',
          }),
        },
      },
      [QuotesApiActionTypes.ProposalUpdatedFailure]: {
        base: {
          create: () => ({
            text: $localize`Proposal form failed to save`,
            type: 'info',
          }),
        },
      },
      [QuotesApiActionTypes.ProposalAutoSavedSuccess]: {
        base: {
          create: () => ({
            text: $localize`Proposal form successfully saved`,
            type: 'info',
          }),
        },
      },
      [QuotesApiActionTypes.ProposalAutoSavedFailure]: {
        base: {
          create: () => ({
            text: $localize`Proposal form failed to save`,
            type: 'info',
          }),
        },
      },
      [QuotesApiActionTypes.SendRenewalInviteSuccess]: {
        base: {},
        variants: {
          [Partners.LSOS_ADDON]: {
            base: {
              create: this.genericStatusUpdateCreator,
            },
          },
        },
      },
      [QuotesApiActionTypes.ProposalSubmittedSuccess]: {
        base: {},
        variants: {
          [Partners.LSOS_ADDON]: {
            base: {
              create: this.genericStatusUpdateCreator,
            },
          },
        },
      },
      [QuotesApiActionTypes.QuoteRequestedSuccess]: {
        base: {},
        variants: {
          [Partners.LSOS_ADDON]: {
            base: {
              create: this.genericStatusUpdateCreator,
            },
          },
        },
      },
      [QuotesApiActionTypes.QuoteReadySuccess]: {
        base: {},
        variants: {
          [Partners.LSOS_ADDON]: {
            base: {
              create: this.genericStatusUpdateCreator,
            },
          },
        },
      },
      [QuotesApiActionTypes.SendToClientSuccess]: {
        base: {},
        variants: {
          [Partners.LSOS_ADDON]: {
            base: {
              create: this.genericStatusUpdateCreator,
            },
          },
        },
      },
      [QuotesApiActionTypes.AcceptSuccess]: {
        base: {},
        variants: {
          [Partners.LSOS_ADDON]: {
            base: {
              create: this.genericStatusUpdateCreator,
            },
          },
        },
      },
      [QuotesApiActionTypes.NotTakenUpSuccess]: {
        base: {},
        variants: {
          [Partners.LSOS_ADDON]: {
            base: {
              create: this.genericStatusUpdateCreator,
            },
          },
        },
      },
      [QuotesApiActionTypes.InvoiceSuccess]: {
        base: {},
        variants: {
          [Partners.LSOS_ADDON]: {
            base: {
              create: this.genericStatusUpdateCreator,
            },
          },
        },
      },
      [QuotesApiActionTypes.CompleteSuccess]: {
        base: {},
        variants: {
          [Partners.LSOS_ADDON]: {
            base: {
              create: this.genericStatusUpdateCreator,
            },
          },
        },
      },
      [QuotesApiActionTypes.EndorsementsUpdatedSuccess]: {
        base: {},
        variants: {
          [Partners.ACCA]: {
            base: {
              create: () => ({
                text: $localize`Endorsements successfully updated`,
                type: 'success',
              }),
            },
          },
        },
      },
      [QuotesApiActionTypes.EndorsementsUpdatedFailure]: {
        base: {
          create: () => ({
            text: $localize`Endorsements failed to save`,
            type: 'error',
          }),
        },
      },
    },
  });

  private get timings() {
    let nextSlot = this.nextSlot;

    if (differenceInMilliseconds(nextSlot, new Date()) < 0) {
      nextSlot = new Date();
    }

    const delay = clamp(
      differenceInMilliseconds(nextSlot, new Date()),
      0,
      Infinity
    );

    nextSlot = addMilliseconds(
      nextSlot,
      DEFAULT_SUBTLE_MESSAGE_OPTIONS.duration
    );

    return {
      delay,
      nextSlot,
    };
  }

  private genericStatusUpdateCreator(quote: Quote): Partial<FeedbackMessage> {
    return {
      text: $localize`Status successfully updated to ${
        this.workflowMeta.transform(
          quote.workflowStatus,
          quote.type,
          quote.portalId
        ).label
      }`,
      type: 'success',
    };
  }
}
